<template>
  <Transition name="slide-up">
    <!-- Android / Chrome / Edge: native install prompt -->
    <div v-if="mode === 'native'" class="pwa-banner">
      <div class="pwa-left">
        <div class="pwa-icon">
          <span class="material-symbols-outlined" style="font-size:22px;color:#0058be">install_mobile</span>
        </div>
        <div class="pwa-text">
          <div class="pwa-title">Install Personal Finance</div>
          <div class="pwa-sub">Add to home screen for a native app experience</div>
        </div>
      </div>
      <div class="pwa-actions">
        <button class="pwa-later" @click="dismiss">Later</button>
        <button class="pwa-install" @click="install">Install</button>
      </div>
    </div>

    <!-- iOS / Safari: manual instructions -->
    <div v-else-if="mode === 'ios'" class="pwa-banner pwa-banner-ios">
      <div class="pwa-left">
        <div class="pwa-icon">
          <span class="material-symbols-outlined" style="font-size:22px;color:#0058be">ios_share</span>
        </div>
        <div class="pwa-text">
          <div class="pwa-title">Add to Home Screen</div>
          <div class="pwa-sub">
            Tap <span class="pwa-share-icon">&#x2B06;</span> Share then <strong>"Add to Home Screen"</strong>
          </div>
        </div>
      </div>
      <button class="pwa-close" @click="dismiss" aria-label="Dismiss">
        <span class="material-symbols-outlined" style="font-size:18px">close</span>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

type Mode = 'native' | 'ios' | null

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'pwa-install-dismissed'

const mode = ref<Mode>(null)
let deferredPrompt: BeforeInstallPromptEvent | null = null

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
}

function wasDismissed() {
  return !!localStorage.getItem(DISMISSED_KEY)
}

function onBeforeInstall(e: Event) {
  e.preventDefault()
  deferredPrompt = e as BeforeInstallPromptEvent
  if (!wasDismissed()) mode.value = 'native'
}

onMounted(() => {
  if (isStandalone() || wasDismissed()) return

  if (isIos()) {
    // Small delay so it doesn't pop immediately on load
    setTimeout(() => { mode.value = 'ios' }, 2000)
  } else {
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
  }
})

onUnmounted(() => window.removeEventListener('beforeinstallprompt', onBeforeInstall))

async function install() {
  if (!deferredPrompt) return
  await deferredPrompt.prompt()
  await deferredPrompt.userChoice
  deferredPrompt = null
  dismiss()
}

function dismiss() {
  localStorage.setItem(DISMISSED_KEY, '1')
  mode.value = null
}
</script>

<style scoped>
.pwa-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 48px);
  max-width: 560px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 9999;
}

.pwa-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.pwa-icon {
  width: 42px;
  height: 42px;
  background: #eef2ff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pwa-text { min-width: 0; }

.pwa-title {
  font-family: Lexend, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #091426;
}

.pwa-sub {
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 2px;
}

.pwa-share-icon {
  display: inline-block;
  font-size: 0.85rem;
  vertical-align: middle;
}

.pwa-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pwa-later {
  font-family: Inter, sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
}
.pwa-later:hover { background: #f3f4f6; }

.pwa-install {
  font-family: Inter, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  background: #0058be;
  border: none;
  cursor: pointer;
  padding: 7px 16px;
  border-radius: 8px;
  transition: background 0.12s;
}
.pwa-install:hover { background: #0047a0; }

.pwa-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
}
.pwa-close:hover { background: #f3f4f6; color: #374151; }

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
}
</style>
